import { useRunActionsOnAgendaRecordMutation } from 'store/services/flow/agendaRecords';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FieldValues } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import type { RecordFormItem } from 'types';

import { validateMessage } from 'utils/agendaRecord/validateMessage';
import { handleSaveError } from 'utils/agendaRecord/handleSaveError';
import { handleRedirectActions } from 'utils/agendaRecord/handleRedirectActions';
import { getAttributeValue, getPortalSafeValue, initialAttributeValues } from 'utils/agendaRecord/agendaRecord';
import { toLowerCamelCase } from 'utils/string';

interface UseSubmitToRunActionsParams {
  agendaId: string;
  recordId: string;
  items: RecordFormItem[];
  resetForms: () => void;
  refetch?: () => void;
  lockToken?: string | null;
  onLockConflict?: () => void;
}

export const useSubmitToRunActions = ({ agendaId, recordId, items, resetForms, refetch, lockToken, onLockConflict }: UseSubmitToRunActionsParams) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [runActionsOnAgendaRecord, { isLoading }] = useRunActionsOnAgendaRecordMutation();

  const submitToRunActions = useCallback(
    async (values: FieldValues, clickedButtonId?: string) => {
      const attributes = items
        .filter(({ formItem }) => formItem.isActionInput === true || formItem.id === clickedButtonId)
        .map(({ formItem }) => {
          const type = formItem.definition.type;
          const isClickedButton = formItem.id === clickedButtonId && type === 'Button';
          const value = isClickedButton ? true : values[formItem.id];
          const payloadValue = getPortalSafeValue(formItem.definition, value);

          const attributeName = `${toLowerCamelCase(type)}AttributeInput`;
          return {
            formItemId: formItem.id,
            ...initialAttributeValues,
            [attributeName]:
              (payloadValue !== undefined && payloadValue !== null) || type === 'Button' ? getAttributeValue(type, payloadValue) : null,
          };
        });

      const res = await runActionsOnAgendaRecord({
        agendaId,
        body: {
          status: 'Active',
          attributes,
        },
        recordId,
        ...(lockToken ? { lockToken } : {}),
      });

      if (!('error' in res)) {
        try {
          validateMessage(res.data);

          toast.success(t('toast.updated.record'));
          resetForms();
          refetch?.();

          handleRedirectActions(res.data, navigate);
        } catch (error) {
          handleSaveError(error, {
            onActionError: () => toast.error(t('toast.error.action')),
            onUnknownError: () => toast.error(t('toast.error.action')),
            onLockConflict,
          });
        }
      } else {
        handleSaveError(res.error, {
          onActionError: () => toast.error(t('toast.error.action')),
          onUnknownError: () => toast.error(t('toast.error.action')),
          onLockConflict,
        });
      }
    },
    [items, runActionsOnAgendaRecord, agendaId, recordId, t, resetForms, refetch, navigate, lockToken, onLockConflict]
  );

  return { submitToRunActions, isLoading };
};
